import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Palette, Plus } from 'lucide-react';

import CommonButton from '../../../../../components/common-button/CommonButton';
import ColorInput from '../../../../../components/color-input/ColorInput';
import CommonLoader from '../../../../../components/common-loader/CommonLoader';
import ConfirmationModal from '../../../../../components/confirmation-modal/ConfirmationModal';
import { Input } from '../../../../../components/common-inputs';
import EmptyState from '../../../../../components/empty-state/EmptyState';
import DeleteLabel from '../../../../../projects/projects-list/components/DeleteLabel';
import {
  addMediaStudioColor,
  deleteMediaStudioColor,
  getMediaStudioColors,
} from '../../../../../redux/settings/media-studio/mediaStudioSettingsActions';
import AssetCard from '../asset-card/AssetCard';
import AssetGrid from '../asset-grid/AssetGrid';

import scss from './BrandColors.module.scss';

const DEFAULT_NEW_COLOR = '#FDBF47';

function BrandColors() {
  const { t } = useTranslation('settings');
  const dispatch = useDispatch();
  const { colors, isColorsLoading, isColorAdding, deletingColorIds } = useSelector(
    (state) => state.mediaStudioSettings,
  );
  const [label, setLabel] = useState('');
  const [color, setColor] = useState(DEFAULT_NEW_COLOR);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    dispatch(getMediaStudioColors());
  }, [dispatch]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!label.trim()) {
      return;
    }
    const result = await dispatch(addMediaStudioColor({ label: label.trim(), color }));
    if (addMediaStudioColor.fulfilled.match(result)) {
      setLabel('');
    }
  };

  const renderContent = () => {
    if (isColorsLoading) {
      return <CommonLoader isLoading />;
    }

    if (!colors.length) {
      return (
        <EmptyState
          icon={<Palette size={28} strokeWidth={1.5} />}
          title={t('mediaStudioNoColors')}
          description={t('mediaStudioNoColorsHint')}
        />
      );
    }

    return (
      <AssetGrid isCompact>
        {colors.map((brandColor) => (
          <AssetCard
            key={brandColor.id}
            title={brandColor.label}
            meta={brandColor.color.toUpperCase()}
            isLoading={deletingColorIds.includes(brandColor.id)}
            onDelete={() => setDeleteTarget(brandColor)}>
            <span className={scss.swatch} style={{ background: brandColor.color }} />
          </AssetCard>
        ))}
      </AssetGrid>
    );
  };

  return (
    <div className={scss.section}>
      <form className={scss.form} onSubmit={handleSubmit}>
        <Input value={label} onChange={setLabel} placeholder={t('mediaStudioColorName')} size='sm' maxWidth={240} />
        <ColorInput color={color} onChange={setColor} maxWidth={180} disableAlpha />
        <CommonButton
          type='submit'
          maxWidth={140}
          disabled={!label.trim()}
          isLoading={isColorAdding}
          LeftIcon={<Plus size={16} />}
          isStrokeIcon>
          {t('mediaStudioAddColor')}
        </CommonButton>
      </form>

      <div className={scss.list}>{renderContent()}</div>

      <ConfirmationModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => dispatch(deleteMediaStudioColor(deleteTarget.id))}
        subLabel={<DeleteLabel name={deleteTarget?.label} />}
      />
    </div>
  );
}

export default BrandColors;
